import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, Loader2, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const field = "w-full h-11 rounded-xl border border-slate-200 bg-surface-deep/60 px-3.5 pr-10 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-gold-400";

const PwInput = ({ id, value, onChange, placeholder, show }) => (
  <input data-testid={id} type={show ? "text" : "password"} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} autoComplete={id.includes("current") ? "current-password" : "new-password"} className={field} />
);

export const PasswordCard = () => {
  const { user } = useAuth();
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const needCurrent = user?.has_password !== false;
  const err = pw && pw.length < 8 ? "At least 8 characters" : pw2 && pw !== pw2 ? "Passwords don't match" : "";
  const ok = pw.length >= 8 && pw === pw2 && (!needCurrent || cur);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/auth/change-password", { current_password: cur, new_password: pw }); toast.success("Password changed"); setCur(""); setPw(""); setPw2(""); }
    catch (e2) { toast.error(formatApiError(e2)); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} data-testid="password-card" className="card min-w-0 p-5 sm:p-6">
      <div className="flex items-center gap-3">
        <span className="grid place-items-center w-10 h-10 shrink-0 rounded-xl bg-gold-400/15 text-gold-300"><KeyRound className="w-5 h-5" /></span>
        <div className="flex-1"><h3 className="font-semibold text-slate-900">{needCurrent ? "Change password" : "Set a password"}</h3>
          <p className="text-sm text-slate-500">{needCurrent ? "Enter your current password, then a new one." : "You sign in with Google. Add a password to sign in with your email too."}</p></div>
        <button type="button" data-testid="password-toggle-show" onClick={() => setShow(!show)} aria-label={show ? "Hide passwords" : "Show passwords"} className="grid place-items-center w-9 h-9 rounded-lg text-slate-500 hover:bg-slate-100">{show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
      </div>
      <div className="mt-4 space-y-2.5">
        {needCurrent && <PwInput id="password-current-input" value={cur} onChange={setCur} placeholder="Current password" show={show} />}
        <PwInput id="password-new-input" value={pw} onChange={setPw} placeholder="New password (8+ characters)" show={show} />
        <PwInput id="password-confirm-input" value={pw2} onChange={setPw2} placeholder="Type the new password again" show={show} />
        {err && <p data-testid="password-error" className="text-xs font-semibold text-rose-700">{err}</p>}
      </div>
      <button data-testid="password-submit" disabled={busy || !ok} className="mt-4 inline-flex items-center gap-2 rounded-full bg-gold-400 px-5 py-2.5 text-sm font-bold text-black hover:bg-gold-300 disabled:opacity-40">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}{needCurrent ? "Change password" : "Set password"}
      </button>
    </form>
  );
};
